/**
 * The project chat on the wire (docs/collaboration.md «Project chat»): four events in rt's own envelope
 * `{ type, key?, data }`, so the relay that carries the app's multiplayer can carry them as they are
 * (docs/tasks/project-chat). A client sends them bare; the relay stamps who sent each one and when it passed,
 * which the receiving side trusts instead of anything the sender claims about itself.
 */

/** The reactions the picker offers first, Instagram's: a double click on a message is the first of them. */
export const CHAT_QUICK_REACTIONS = ['❤️', '😂', '😮', '😢', '😡', '👍'] as const;
/** The rest, behind the picker's «+». */
export const CHAT_MORE_REACTIONS = ['🔥', '👏', '🎉', '🙏', '😍', '🥰', '😊', '😅', '🤔', '👀', '✅', '❌', '💯', '🙌',
  '😎', '🤩', '🥳', '😬', '🤝', '💪', '✨', '🏠', '🛋️', '🎨'] as const;
/** Every reaction a message can get; one outside the set is ignored. */
export const CHAT_REACTIONS = [...CHAT_QUICK_REACTIONS, ...CHAT_MORE_REACTIONS] as const;
export type ChatReaction = typeof CHAT_REACTIONS[number];
export const isChatReaction = (value: unknown): value is ChatReaction => CHAT_REACTIONS.includes(value as ChatReaction);

/** Characters in one message: the composer stops there, a longer message from elsewhere is refused. */
export const CHAT_TEXT_LIMIT = 2000;
/** Messages a client keeps, the relay's history included; older ones fall off with their reactions. */
export const CHAT_HISTORY_LIMIT = 500;
/** A «печатает…» that is not repeated disappears after this long, so a lost «stopped» cannot stick. */
export const TYPING_TTL_MS = 6000;
/** How often the one typing repeats that they are, while they keep typing. */
export const TYPING_REFRESH_MS = 2500;
/** No keystroke for this long and the one typing is no longer typing. */
export const TYPING_IDLE_MS = 4000;

/** A message, a new one or its next version: an edit keeps the id and adds `editedAt`, a deletion leaves `deleted`. */
export type ChatMessageData = { id: string; text: string; replyTo: string | null; sentAt: number; editedAt?: number; deleted?: true };
/** One person's reaction to one message: each has one at most, a new one replaces it, null takes it off. */
export type ChatReactionData = { messageId: string; emoji: ChatReaction | null };
/** Read up to this message; `at` is its place in the history, so the mark compares even before the message arrives. */
export type ChatReadData = { messageId: string; at: number };
export type ChatTypingData = { typing: boolean };

/** What a client sends. `key` is rt's replace-by-key identity: the message id (for a reaction, the relay adds its author). */
export type ChatOutgoing =
  | { type: 'ChatMessage'; key: string; data: ChatMessageData }
  | { type: 'ChatReaction'; key: string; data: ChatReactionData }
  | { type: 'ChatRead'; data: ChatReadData }
  | { type: 'ChatTyping'; data: ChatTypingData };

/**
 * What a client receives: the same event, stamped by the relay with its author and the relay's clock. `at` grows
 * strictly within a project's chat — two events never share it — and a message keeps the `at` of its first version,
 * so edits and deletions stay where the message was.
 */
export type ChatIncoming = ChatOutgoing & { userId: string; displayName: string; at: number };

export type ChatConnection = 'connecting' | 'connected' | 'disconnected';

/**
 * Where the chat's events go and come from: the rt relay once the web joins the app's multiplayer, the demo
 * participants until then (`demo.ts`). `subscribe` delivers the stored history first, then live events, and
 * reports the connection; the relay echoes the client's own events back, which confirms a message.
 */
export interface ChatTransport {
  readonly self: { userId: string; displayName: string };
  /** Hands the events to the relay; rejects when they could not leave (no connection). */
  send(events: ChatOutgoing[]): Promise<void>;
  subscribe(receive: (events: ChatIncoming[]) => void, status: (value: ChatConnection) => void): () => void;
  /** Tries to connect again at once, after the connection was lost. */
  reconnect?(): void;
  dispose(): void;
}

const text = (value: unknown, max: number): value is string => typeof value === 'string' && value.length <= max;
const time = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0;
const id = (value: unknown): value is string => text(value, 64) && value.length > 0;

/** Whether an event from the relay is one this client understands; anything else is dropped whole. */
export function isChatIncoming(value: unknown): value is ChatIncoming {
  if (!value || typeof value !== 'object') return false;
  const event = value as Partial<ChatIncoming> & { data?: Record<string, unknown> };
  if (!id(event.userId) || !text(event.displayName, 120) || !time(event.at) || !event.data || typeof event.data !== 'object') return false;
  const data = event.data;
  switch (event.type) {
    case 'ChatMessage':
      return id(data.id) && text(data.text, CHAT_TEXT_LIMIT) && (data.replyTo === null || id(data.replyTo)) && time(data.sentAt)
        && (data.editedAt === undefined || time(data.editedAt)) && (data.deleted === undefined || data.deleted === true);
    case 'ChatReaction': return id(data.messageId) && (data.emoji === null || isChatReaction(data.emoji));
    case 'ChatRead': return id(data.messageId) && time(data.at);
    case 'ChatTyping': return typeof data.typing === 'boolean';
    default: return false;
  }
}
